/**
 * Workbench Bill of Materials panel — Iter 247.
 *
 * Persistent, append-only-but-editable parts list that lives under
 * the StageChat. Auto-refreshes every 4s while the chat is active so
 * the rep sees the AI extractor's updates land in near real-time as
 * they chat.
 *
 * Editing: inline qty bump, click-to-remove (soft delete with undo),
 * manual "Add line" composer at the bottom, "Show removed" toggle to
 * restore. Estimate stage gets an extra "Search inventory" affordance
 * per non-stock row so the rep can resolve unmatched items manually.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ListChecks, Plus, Trash2, RotateCcw, Loader2, Search, X,
  AlertTriangle, Edit3, Save, Sparkles, Eye, EyeOff, Wand2, Wrench,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import SolverModal from "./SolverModal";

function classNames(...c) { return c.filter(Boolean).join(" "); }

function fmtZAR(n) {
  if (n == null || isNaN(n)) return "—";
  return "R " + Number(n).toLocaleString("en-ZA", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function BomItemRow({ draft, item, onChanged, onSearch }) {
  // Iter 290zp.15z02 — sku_match comes back from auto-match as an OBJECT
  // ({inventory_id, sku, name, confidence, method, reason, ts}), but
  // manual entry sends a plain string. Normalise both forms for display
  // and for the inline edit input so the panel never tries to render an
  // object as a React child.
  const skuDisplay = typeof item.sku_match === "object" && item.sku_match
    ? (item.sku_match.sku || item.sku_match.name || item.sku_match.inventory_id || "")
    : (item.sku_match || "");
  const skuMethod = typeof item.sku_match === "object" && item.sku_match
    ? item.sku_match.method : null;
  const skuConfidence = typeof item.sku_match === "object" && item.sku_match
    ? item.sku_match.confidence : null;

  const [editing, setEditing] = useState(false);
  const [qty, setQty] = useState(item.qty ?? 1);
  const [unit, setUnit] = useState(item.unit || "pcs");
  const [desc, setDesc] = useState(item.description || "");
  const [sku, setSku] = useState(skuDisplay);
  const [busy, setBusy] = useState(false);

  const isRemoved = item.status === "removed";
  const isNonStock = item.bucket === "non-stock";
  const isAi = item.source === "ai";

  const save = async () => {
    const cleanDesc = (desc || "").trim();
    if (!cleanDesc) {
      toast.error("Description can't be empty.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/workbench/drafts/${draft.id}/bom/${item.id}`,
        {
          qty: parseFloat(qty) || 0,
          unit: (unit || "pcs").trim().slice(0, 20),
          description: cleanDesc,
          sku_match: (sku || "").trim() || null,
        },
      );
      onChanged?.(data);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  const cancelEdit = () => {
    setQty(item.qty ?? 1);
    setUnit(item.unit || "pcs");
    setDesc(item.description || "");
    setSku(skuDisplay);
    setEditing(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/workbench/drafts/${draft.id}/bom/${item.id}`);
      onChanged?.({ ...item, status: "removed" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove.");
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draft.id}/bom/${item.id}/restore`,
        {},
      );
      onChanged?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't restore.");
    } finally {
      setBusy(false);
    }
  };

  // Iter 248.1 — when editing, render an EXPANDED panel with clearly
  // labelled qty / unit / description / SKU-match controls instead of
  // cramming them into the row. The rep can now actually see what
  // they're editing (Dirk's feedback: "you can only edit the quantity").
  if (editing) {
    return (
      <div
        className="rounded-md border border-cyan-400/40 bg-cyan-500/[0.04] p-2.5 space-y-2"
        data-testid={`bom-row-${item.id}-edit`}
      >
        <div className="flex flex-wrap gap-2 items-end">
          <div className="space-y-0.5">
            <label className="text-[9px] uppercase tracking-wider text-[#647087] block">Qty</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="hv-input w-20 text-xs py-1"
              data-testid={`bom-qty-input-${item.id}`}
            />
          </div>
          <div className="space-y-0.5">
            <label className="text-[9px] uppercase tracking-wider text-[#647087] block">Unit</label>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="hv-input w-20 text-xs py-1"
              data-testid={`bom-unit-input-${item.id}`}
            >
              {["pcs", "m", "kg", "L", "set", "kit", "box", "roll", "pair", "pack"].map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          <div className="space-y-0.5 flex-1 min-w-[200px]">
            <label className="text-[9px] uppercase tracking-wider text-[#647087] block">
              Description (what the part actually is)
            </label>
            <input
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="e.g. 50mm PVC ball valve Class 6"
              className="hv-input w-full text-xs py-1"
              autoFocus
              data-testid={`bom-desc-input-${item.id}`}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") cancelEdit();
              }}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="space-y-0.5 flex-1 min-w-[200px]">
            <label className="text-[9px] uppercase tracking-wider text-[#647087] block">
              Inventory match (SKU code — optional, leave blank to keep as non-stock)
            </label>
            <div className="flex gap-1.5">
              <input
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder="SKU or leave blank"
                className="hv-input flex-1 text-xs py-1 font-mono"
                data-testid={`bom-sku-input-${item.id}`}
              />
              {onSearch && (
                <button
                  type="button"
                  onClick={() => onSearch(item)}
                  className="hv-btn hv-btn-ghost text-[11px]"
                  data-testid={`bom-search-from-edit-${item.id}`}
                  title="Search inventory"
                >
                  <Search className="w-3 h-3" /> Search inventory
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="hv-btn hv-btn-primary text-[11px]"
            data-testid={`bom-save-${item.id}`}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save changes
          </button>
          <button
            type="button"
            onClick={cancelEdit}
            disabled={busy}
            className="hv-btn hv-btn-ghost text-[11px]"
            data-testid={`bom-cancel-${item.id}`}
          >
            <X className="w-3 h-3" /> Cancel
          </button>
          {!isRemoved && (
            <button
              type="button"
              onClick={() => { cancelEdit(); remove(); }}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-[11px] text-rose-300 hover:text-rose-200 ml-auto"
              data-testid={`bom-remove-from-edit-${item.id}`}
              title="Remove this line from the BoM"
            >
              <Trash2 className="w-3 h-3" /> Remove line
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "rounded-md border px-2.5 py-1.5 text-xs flex items-center gap-2",
        isRemoved
          ? "border-white/5 bg-white/[0.01] text-[#647087] line-through"
          : isNonStock
            ? "border-amber-400/30 bg-amber-500/[0.06] text-[#e4d4a8]"
            : "border-white/10 bg-white/[0.03] text-[#cbd5e1]",
      )}
      data-testid={`bom-row-${item.id}`}
    >
      {/* qty */}
      <span className="font-mono text-cyan-300 w-12 shrink-0">
        {item.qty} {item.unit}
      </span>

      {/* description */}
      <span className="flex-1 truncate" title={item.description}>
        {item.description}
        {skuDisplay && (
          <span
            className="ml-1.5 text-[10px] text-cyan-400/80 font-mono"
            title={
              skuConfidence != null
                ? `Matched via ${skuMethod || "manual"} · confidence ${(skuConfidence * 100).toFixed(0)}%`
                : undefined
            }
          >
            · {skuDisplay}
            {skuMethod === "llm_semantic" && (
              <span className="ml-1 text-purple-300/70" title="Matched by LLM">⌬</span>
            )}
          </span>
        )}
      </span>

      {/* source/badge */}
      {isAi && (
        <span className="text-[9px] uppercase tracking-wider text-purple-300/70" title="Added by Hidro from chat">
          AI
        </span>
      )}
      {isNonStock && (
        <span className="text-[9px] uppercase tracking-wider text-amber-300" title="No inventory match">
          NON-STOCK
        </span>
      )}

      {/* actions */}
      {isRemoved ? (
        <button
          onClick={restore}
          disabled={busy}
          className="text-emerald-300 hover:text-emerald-200 p-1"
          data-testid={`bom-restore-${item.id}`}
          title="Restore"
        >
          <RotateCcw className="w-3 h-3" />
        </button>
      ) : (
        <>
          {isNonStock && onSearch && (
            <button
              onClick={() => onSearch(item)}
              disabled={busy}
              className="text-amber-300 hover:text-amber-200 p-1"
              data-testid={`bom-search-${item.id}`}
              title="Search inventory to resolve"
            >
              <Search className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={() => setEditing(true)}
            disabled={busy}
            className="text-[#647087] hover:text-cyan-300 p-1"
            data-testid={`bom-edit-${item.id}`}
            title="Edit qty, unit, description, SKU"
          >
            <Edit3 className="w-3 h-3" />
          </button>
          <button
            onClick={remove}
            disabled={busy}
            className="text-[#647087] hover:text-rose-300 p-1"
            data-testid={`bom-remove-${item.id}`}
            title="Remove"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </>
      )}
    </div>
  );
}

function InventorySearchSheet({ draft, item, onClose, onPicked }) {
  const [q, setQ] = useState(item?.description || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async () => {
    if (!q || q.length < 2) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/inventory", {
        params: { search: q, page_size: 25 },
      });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setRows(items);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed.");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const handle = setTimeout(() => { search(); }, 350);
    return () => clearTimeout(handle);
  }, [search]);

  const pick = async (invRow) => {
    try {
      const { data } = await api.patch(
        `/workbench/drafts/${draft.id}/bom/${item.id}`,
        {
          sku_match: invRow.sku || `id:${invRow.id}`,
          description: invRow.name || item.description,
          bucket: "open",
          sell_price_incl: invRow.sell_price_incl || null,
          cost: invRow.avg_cost || invRow.last_cost || null,
        },
      );
      toast.success(`Matched to ${invRow.sku || invRow.name}`);
      onPicked?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't match.");
    }
  };

  return (
    <div className="rounded-lg border border-amber-400/30 bg-amber-500/[0.04] p-3 space-y-2" data-testid="bom-inventory-search">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-amber-300" />
        <h3 className="text-xs font-semibold">Resolve "{item?.description}"</h3>
        <button onClick={onClose} className="ml-auto text-[#647087] hover:text-white" data-testid="bom-search-close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search SKU, name, manufacturer, description…"
        className="hv-input text-xs w-full"
        autoFocus
        data-testid="bom-search-input"
      />
      {loading ? (
        <div className="text-[11px] text-[#647087] flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Searching…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-[11px] text-[#647087]">
          {q.length < 2 ? "Type to search…" : "No matches. Try a different name or SKU prefix."}
        </p>
      ) : (
        <div className="max-h-72 overflow-y-auto space-y-1 pr-1">
          {rows.map((r) => (
            <button
              key={r.id}
              onClick={() => pick(r)}
              className="w-full text-left rounded-md border border-white/10 bg-black/30 hover:border-cyan-400/40 hover:bg-cyan-500/[0.05] px-2 py-1.5 transition"
              data-testid={`bom-pick-${r.id}`}
            >
              <div className="flex items-center gap-2 text-[11px]">
                <span className="font-mono text-cyan-300">{r.sku || "no-sku"}</span>
                <span className="flex-1 truncate text-[#cbd5e1]">{r.name}</span>
                <span className="text-emerald-300 font-mono">
                  {r.sell_price_incl > 0 ? fmtZAR(r.sell_price_incl) : "—"}
                </span>
              </div>
              {r.description && (
                <div className="text-[10px] text-[#647087] truncate mt-0.5">{r.description}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BomPanel({ draft }) {
  const [items, setItems] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRemoved, setShowRemoved] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newDesc, setNewDesc] = useState("");
  const [newQty, setNewQty] = useState(1);
  const [searchTarget, setSearchTarget] = useState(null);
  // Iter 290zp.15z02 — Auto-match Inventory FE button.
  const [autoMatching, setAutoMatching] = useState(false);
  const [autoMatchResult, setAutoMatchResult] = useState(null);
  // Iter 290zp.15z03 — Layer C plumbing solver modal.
  const [solverOpen, setSolverOpen] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/workbench/drafts/${draft.id}/bom`,
        { params: { include_removed: true, _: Date.now() },
          headers: { "Cache-Control": "no-cache" } },
      );
      setItems(Array.isArray(data?.items) ? data.items : []);
      setUpdatedAt(data?.updated_at || null);
    } catch {
      // keep last state
    } finally {
      setLoading(false);
    }
  }, [draft.id]);

  useEffect(() => { load(); }, [load]);

  // Iter 247 — keep BoM in sync with the AI extractor running on the
  // backend. 4s cadence so the rep sees additions land near-real-time
  // without hammering the server. Visibility-aware so a backgrounded
  // tab doesn't tick uselessly.
  useEffect(() => {
    if (collapsed) return undefined;
    const tick = () => { if (!document.hidden) load(); };
    pollRef.current = setInterval(tick, 4000);
    const onWake = () => tick();
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [load, collapsed]);

  const onRowChanged = (updated) => {
    setItems((prev) => prev.map((it) => it.id === updated.id ? { ...it, ...updated } : it));
  };

  const addLine = async () => {
    const d = (newDesc || "").trim();
    if (!d) return;
    try {
      const { data } = await api.post(`/workbench/drafts/${draft.id}/bom`, {
        qty: parseFloat(newQty) || 1, unit: "pcs", description: d,
      });
      setItems((prev) => [...prev, data]);
      setNewDesc(""); setNewQty(1); setAdding(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add.");
    }
  };

  /**
   * Iter 290zp.15z02 — Auto-match Inventory.
   *
   * Asks the backend to run a semantic LLM pass over every non-stock /
   * unmatched BoM row, comparing the description against the live
   * inventory_attrs catalogue and stamping `sku_match` + `bucket="open"`
   * when the model returns a high-confidence hit. `force=true` re-runs
   * every row including ones that already matched (useful after the
   * rep enriches inventory with new SKUs).
   */
  const runAutoMatch = useCallback(async (force = false) => {
    if (autoMatching) return;
    setAutoMatching(true);
    setAutoMatchResult(null);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draft.id}/bom/auto-match`,
        null,
        { params: { force }, timeout: 90000 },
      );
      setAutoMatchResult(data || {});
      const heuristicMatched = data?.matched ?? 0;
      const llmMatched = data?.llm_picked ?? 0;
      const totalMatched = heuristicMatched + llmMatched;
      const skipped = data?.skipped ?? 0;
      const leftOver = data?.non_stock_left ?? 0;
      if (totalMatched > 0) {
        toast.success(
          `Matched ${totalMatched} row${totalMatched === 1 ? "" : "s"}` +
          (llmMatched > 0 ? ` (${llmMatched} via LLM)` : "") +
          (leftOver > 0 ? ` · ${leftOver} still non-stock` : "")
        );
      } else if (leftOver === 0 && skipped === 0) {
        toast.info("Nothing to match — every line already has a SKU.");
      } else {
        toast.info(`No new matches. ${leftOver} non-stock row${leftOver === 1 ? "" : "s"} left.`);
      }
      // Refresh after the backend rewrites BoM rows.
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-match failed.");
    } finally {
      setAutoMatching(false);
    }
  }, [draft.id, autoMatching, load]);

  const visibleItems = items.filter((it) => showRemoved || it.status !== "removed");
  const openCount = items.filter((it) => it.status !== "removed").length;
  const removedCount = items.filter((it) => it.status === "removed").length;
  const nonStockCount = items.filter((it) => it.bucket === "non-stock" && it.status !== "removed").length;
  // Auto-match only matters when at least one non-stock or unmatched row exists.
  const autoMatchCandidates = useMemo(
    () => items.filter((it) => {
      if (it.status === "removed") return false;
      if (it.bucket === "non-stock") return true;
      if (it.is_non_stock) return true;
      // sku_match may be a string (manual) or an object (auto-match).
      // A non-stock row gets an object with inventory_id=null — still
      // a candidate for re-matching.
      const m = it.sku_match;
      if (!m) return true;
      if (typeof m === "object" && !m.inventory_id) return true;
      return false;
    }).length,
    [items],
  );

  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3 sm:p-4 space-y-3" data-testid="workbench-bom-panel">
      <header className="flex items-center gap-2">
        <ListChecks className="w-4 h-4 text-emerald-300" />
        <h2 className="text-sm font-semibold">Bill of materials</h2>
        <span className="text-[10px] text-[#647087]">
          {openCount} open
          {nonStockCount > 0 && ` · ${nonStockCount} non-stock`}
          {removedCount > 0 && ` · ${removedCount} removed`}
        </span>
        <span className="text-[10px] text-[#475569] ml-auto">
          {updatedAt ? new Date(updatedAt).toLocaleTimeString() : ""}
        </span>
        {/* Iter 290zp.15z03 — Generate-from-build (Layer C solver). */}
        <button
          type="button"
          onClick={() => setSolverOpen(true)}
          className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-md border border-purple-500/40 bg-purple-500/10 text-purple-200 hover:bg-purple-500/15 transition-colors"
          data-testid="workbench-bom-generate-from-build"
          title="Type a build description; the solver parses it + picks real SKUs."
        >
          <Wrench className="w-3 h-3" />
          <span>Generate from build</span>
        </button>
        {/* Iter 290zp.15z02 — Auto-match Inventory button. */}
        <button
          type="button"
          onClick={(e) => runAutoMatch(e.shiftKey)}
          disabled={autoMatching || autoMatchCandidates === 0}
          title={
            autoMatchCandidates === 0
              ? "Every line already has a SKU — nothing to match."
              : "Run the LLM-assisted inventory matcher across unmatched rows. Shift-click to force re-evaluate all rows."
          }
          className={classNames(
            "inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-md border transition-colors",
            autoMatchCandidates === 0 || autoMatching
              ? "border-white/10 text-[#647087]"
              : "border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/15",
          )}
          data-testid="workbench-bom-automatch"
        >
          {autoMatching
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <Wand2 className="w-3 h-3" />}
          <span>
            {autoMatching ? "Matching…" : `Auto-match${autoMatchCandidates > 0 ? ` (${autoMatchCandidates})` : ""}`}
          </span>
        </button>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="text-[#647087] hover:text-white p-0.5"
          data-testid="workbench-bom-collapse"
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>
      </header>

      {!collapsed && (
        <>
          {/* Auto-match result banner — appears briefly under the header
              after a run so the rep can see counts without scrolling. */}
          {autoMatchResult && (
            <div
              className="text-[11px] flex items-center gap-1.5 px-2 py-1 rounded border border-cyan-400/30 bg-cyan-500/5 text-cyan-200"
              data-testid="workbench-bom-automatch-result"
            >
              <Wand2 className="w-3 h-3" />
              <span>
                Matched <b>{(autoMatchResult.matched ?? 0) + (autoMatchResult.llm_picked ?? 0)}</b> row{((autoMatchResult.matched ?? 0) + (autoMatchResult.llm_picked ?? 0)) === 1 ? "" : "s"}
                {autoMatchResult.llm_picked > 0 && ` (${autoMatchResult.llm_picked} via LLM)`}
                {autoMatchResult.non_stock_left > 0 && ` · ${autoMatchResult.non_stock_left} non-stock left`}
                {autoMatchResult.skipped > 0 && ` · ${autoMatchResult.skipped} skipped`}.
              </span>
              <button
                type="button"
                onClick={() => setAutoMatchResult(null)}
                className="ml-auto text-cyan-300/60 hover:text-cyan-100"
                aria-label="Dismiss"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {nonStockCount > 0 && (
            <div className="text-[11px] text-amber-200/80 flex items-center gap-1.5" data-testid="bom-nonstock-warning">
              <AlertTriangle className="w-3 h-3" />
              {nonStockCount} item{nonStockCount === 1 ? "" : "s"} need to be resolved against inventory before estimate.
            </div>
          )}

          {loading ? (
            <div className="text-xs text-[#647087]">Loading…</div>
          ) : visibleItems.length === 0 ? (
            <p className="text-xs text-[#647087]">
              Hidro will start adding parts here as you chat. You can also add or edit lines manually.
            </p>
          ) : (
            <div className="space-y-1.5" data-testid="bom-list">
              {visibleItems.map((it) => (
                <BomItemRow
                  key={it.id}
                  draft={draft}
                  item={it}
                  onChanged={onRowChanged}
                  onSearch={(target) => setSearchTarget(target)}
                />
              ))}
            </div>
          )}

          {searchTarget && (
            <InventorySearchSheet
              draft={draft}
              item={searchTarget}
              onClose={() => setSearchTarget(null)}
              onPicked={onRowChanged}
            />
          )}

          <div className="flex items-center gap-2 pt-1">
            {adding ? (
              <>
                <input
                  type="number"
                  min={0}
                  value={newQty}
                  onChange={(e) => setNewQty(e.target.value)}
                  className="hv-input w-14 text-[11px] py-0.5"
                  data-testid="bom-add-qty"
                />
                <input
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="e.g. 50mm PVC ball valve Class 6"
                  className="hv-input flex-1 text-[11px] py-0.5"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") addLine(); if (e.key === "Escape") setAdding(false); }}
                  data-testid="bom-add-desc"
                />
                <button onClick={addLine} className="hv-btn hv-btn-primary text-[11px]" data-testid="bom-add-save">
                  Add
                </button>
                <button onClick={() => { setAdding(false); setNewDesc(""); }} className="text-[#647087] hover:text-white p-1" data-testid="bom-add-cancel">
                  <X className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <button
                onClick={() => setAdding(true)}
                className="hv-btn hv-btn-ghost text-[11px]"
                data-testid="bom-add-line"
              >
                <Plus className="w-3 h-3" /> Add line
              </button>
            )}
            {removedCount > 0 && (
              <button
                onClick={() => setShowRemoved((s) => !s)}
                className="text-[10px] text-[#647087] hover:text-white ml-auto"
                data-testid="bom-toggle-removed"
              >
                {showRemoved ? "Hide removed" : `Show removed (${removedCount})`}
              </button>
            )}
          </div>

          <p className="text-[10px] text-[#647087] italic flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-purple-300" />
            Hidro updates this list in the background after every chat turn. Edit, remove and restore freely.
          </p>
        </>
      )}

      {/* Iter 290zp.15z03 — Layer C plumbing solver modal mount. */}
      <SolverModal
        open={solverOpen}
        onClose={() => setSolverOpen(false)}
        draftId={draft.id}
        onApplied={async () => { await load(); }}
      />
    </div>
  );
}
